import { useCallback, useEffect, useMemo, useState } from "react";
import { DataTable, DataTablePagination } from "@/src/components/ui/data-table";
import { paymentsFetch } from "@/src/lib/paymentsFetch";
import { RemainingRow } from "../model";
import { remainingColumns } from "./remainingColumns";
import { arrayToCSV, downloadCSV } from "@/src/helpers/downloadCSV";
import { Button } from "@/src/components/ui/button";
import { DownloadIcon } from "lucide-react";
import { buildUtcWindowFromLocalDates } from "./SummaryTable";

type RemainingData = { data: RemainingRow[] } & DataTablePagination;

const PAGE_SIZE = 50;

export function RemainingSummaryTable({ bookedBefore, bookedAfter }: { bookedBefore: string; bookedAfter: string }) {
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState<RemainingData>({
    data: [],
    total: 0,
    pagination: { page: 1, pageSize: PAGE_SIZE },
  });

  const query = useMemo(() => {
    if (bookedAfter || bookedBefore) {
      const { fromUtc, toUtc } = buildUtcWindowFromLocalDates(bookedAfter, bookedBefore);
      return new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
        ...(fromUtc && { fromUtc }),
        ...(toUtc && { toUtc }),
        ...(bookedBefore && { bookedBefore }),
        ...(bookedAfter && { bookedAfter }),
      }).toString();
    } else {
      return "";
    }
  }, [bookedAfter, bookedBefore, page, PAGE_SIZE]);

  const fetchSummary = async () => {
    try {
      return await paymentsFetch<RemainingData>(`/api/payments/summary-remaining?${query}`, "GET");
    } catch (e) {
      console.error("Error fetching payments summary", e);
    }
  };

  useEffect(() => {
    if (page > 1) {
      setPage(1);
    } else {
      if (bookedAfter || bookedBefore) {
        fetchSummary().then((res) => {
          if (res) setSummary(res);
          else
            setSummary({
              data: [],
              total: 0,
              pagination: { page: 1, pageSize: PAGE_SIZE },
            });
        });
      }
    }
  }, [bookedBefore, bookedAfter]);

  const downloadFile = useCallback(() => {
    const csvData = summary.data.map((r) => [
      r.currencies,
      Number(r.remainingInflow).toString(),
      Number(r.remainingOutflow).toString(),
      (r.remainingInflow - r.remainingOutflow).toFixed(2),
    ]);

    csvData.unshift(["Currencies", "Remaining to be received", "Remaining to be paid", "Difference"]);

    downloadCSV(arrayToCSV(csvData), "Remaining Payments Summary");
  }, [summary.data]);

  return (
    <div className="rounded-md bg-white border relative">
      {!!summary.data?.length && (
        <Button className="absolute right-0 -top-12" onClick={downloadFile} size="sm" variant="link">
          <DownloadIcon className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      )}
      <DataTable columns={remainingColumns} data={summary.data} pagination={summary} onPageClick={(p) => setPage(p)} />
    </div>
  );
}
